import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs, query } from "firebase/firestore";
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function Timeline() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  
  const [timelineData, setTimelineData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); 

  // 1. Fetch Employee List
  useEffect(() => {
    const fetchEmps = async () => {
        const q = await getDocs(collection(db, "attendance"));
        const names = new Set();
        q.forEach(doc => {
            if(doc.data().userName) names.add(doc.data().userName);
        });
        setEmployees(Array.from(names).sort());
    };
    fetchEmps();
  }, []);

  // 2. Fetch Timeline Data (ALL MODULES)
  const fetchTimeline = async () => {
    if(!selectedEmp) return alert("Please select an employee");
    
    setLoading(true);
    let allActivities = [];

    const fetchFromCollection = async (colName, type, color, icon) => {
        const q = query(collection(db, colName));
        const snap = await getDocs(q);
        
        snap.forEach(doc => {
            const data = doc.data();
            
            // 🔥 SPECIAL CHECK FOR TASKS (Tasks me 'userName' nahi 'to' hota hai)
            let isUserMatch = data.userName === selectedEmp;
            if(colName === 'tasks') isUserMatch = (data.to === selectedEmp); // Task assigned TO him
            
            if (!isUserMatch) return;

            // Date Check
            let itemDate = "";
            if (data.date) itemDate = data.date.split('T')[0]; 
            else if (data.createdAt) itemDate = data.createdAt.split('T')[0];
            else if (data.inTime) itemDate = data.date; 

            if (itemDate !== selectedDate) return;

            // Extract Time
            let time = "00:00";
            if (data.time) time = data.time;
            else if (data.inTime) time = data.inTime; 
            else if (data.createdAt) {
                const d = new Date(data.createdAt);
                time = d.toLocaleTimeString('en-US', {hour:'2-digit', minute:'2-digit', hour12:true});
            }

            // Create Details
            let details = "";
            
            // --- CORE ---
            if(colName === 'attendance') details = `Punch In: ${data.inTime} | Out: ${data.outTime || 'Active'} | Loc: ${data.location}`;
            
            // --- SALES ---
            if(colName === 'leads') details = `Added Lead: ${data.org} (${data.city})`;
            if(colName === 'sales_reports') details = `Visit: ${data.hospital}. Outcome: ${data.outcome}`;
            if(colName === 'orders') details = `Order: ${data.hospitalName} - ₹${data.amount}. Status: ${data.status}`;
            if(colName === 'demo_notes') details = `Demo: ${data.product} at ${data.hospital}. Status: ${data.status}`;
            
            // --- SERVICE ---
            if(colName === 'installations') details = `Installed: ${data.product} at ${data.hospital}`;
            if(colName === 'pms_reports') details = `PMS: ${data.machine} at ${data.hospital}. Status: ${data.status}`;
            if(colName === 'service_calls') details = `Ticket ${data.scrId}: ${data.hospitalName}. Issue: ${data.remark}`;
            if(colName === 'service_reports') details = `Report ${data.ticketNo}: ${data.issue} - ${data.action}`;

            // --- OFFICE / HR (Added Now) ---
            if(colName === 'travel_notes') details = `Travel: ${data.from} ➝ ${data.to} (${data.mode}). Purpose: ${data.purpose}`;
            if(colName === 'expenses') details = `Expense: ₹${data.amount} (${data.type}). Remark: ${data.remark}`;
            if(colName === 'advances') details = `Advance Req: ₹${data.amount} for ${data.reason}. Status: ${data.status}`;
            if(colName === 'leaves') details = `Leave Req: ${data.days} Days (${data.type}). Reason: ${data.reason}. Status: ${data.status}`;
            if(colName === 'tasks') details = `Task Assigned: "${data.task}" (Priority: ${data.priority}). Status: ${data.status}`;
            if(colName === 'card_requests') details = `Card Req for ${data.shippingAddress}. Status: ${data.status}`;
            if(colName === 'couriers') details = `Courier (${data.type}): ${data.courierName} - Docket: ${data.docketNo}`;

            allActivities.push({
                id: doc.id,
                type: type,
                time: time,
                details: details,
                color: color,
                icon: icon
            });
        });
    };

    // 🔥 FETCHING ALL 12 MODULES
    await fetchFromCollection("attendance", "Attendance", "#34495e", "📅");
    
    await fetchFromCollection("leads", "Lead", "#3498db", "👤");
    await fetchFromCollection("sales_reports", "Visit", "#e67e22", "🤝");
    await fetchFromCollection("orders", "Order", "#27ae60", "🛒");
    await fetchFromCollection("demo_notes", "Demo", "#16a085", "🎭");
    
    await fetchFromCollection("installations", "Install", "#c0392b", "⚙️");
    await fetchFromCollection("pms_reports", "PMS", "#8e44ad", "🛠️");
    await fetchFromCollection("service_calls", "Ticket", "#d35400", "📞");
    await fetchFromCollection("service_reports", "Report", "#e74c3c", "📄");

    await fetchFromCollection("travel_notes", "Travel", "#2980b9", "🚲");
    await fetchFromCollection("expenses", "Expense", "#f1c40f", "💸");
    
    // 🔥 NEW ONES
    await fetchFromCollection("advances", "Advance", "#2ecc71", "💰");
    await fetchFromCollection("leaves", "Leave", "#95a5a6", "🏖️");
    await fetchFromCollection("tasks", "Task", "#8e44ad", "📋");
    await fetchFromCollection("card_requests", "Card", "#34495e", "📇");
    await fetchFromCollection("couriers", "Courier", "#e67e22", "📦");

    // Sort by Time
    allActivities.sort((a, b) => new Date('1970/01/01 ' + a.time) - new Date('1970/01/01 ' + b.time));

    setTimelineData(allActivities);
    setFilteredData(allActivities);
    setLoading(false);
  };

  // 3. 🔥 SUPER SEARCH
  useEffect(() => {
    if (searchTerm) {
        const keywords = searchTerm.toLowerCase().split(" ").filter(w => w);
        const result = timelineData.filter(i => {
            const row = `${i.type} ${i.details} ${i.time}`.toLowerCase();
            return keywords.every(w => row.includes(w));
        });
        setFilteredData(result);
    } else {
        setFilteredData(timelineData);
    }
  }, [searchTerm, timelineData]);

  // 4. EXCEL DOWNLOAD
  const downloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData.map(i => ({
        Date: formatDate(selectedDate),
        Employee: selectedEmp,
        Time: i.time,
        Activity: i.type,
        Details: i.details
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Timeline");
    XLSX.writeFile(wb, `${selectedEmp}_${selectedDate}_Timeline.xlsx`);
  };

  const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString('en-GB');
  };

  return (
    <div style={{padding:"20px", fontFamily:"Arial"}}>
      <h2 style={{color:"#2c3e50", margin:"0 0 20px 0"}}>🕵️‍♂️ Employee 360° Timeline</h2>
      
      {/* CONTROLS */}
      <div style={controlBox}>
        <div style={{flex:1, minWidth:"200px"}}>
            <label style={labelStyle}>Select Employee</label>
            <select value={selectedEmp} onChange={(e)=>setSelectedEmp(e.target.value)} style={inputStyle}>
                <option value="">-- Choose Employee --</option>
                {employees.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
        </div>
        
        <div style={{flex:1, minWidth:"150px"}}>
            <label style={labelStyle}>Select Date</label>
            <input type="date" value={selectedDate} onChange={(e)=>setSelectedDate(e.target.value)} style={inputStyle} />
        </div>
        
        <div style={{display:"flex", alignItems:"flex-end"}}>
            <button onClick={fetchTimeline} style={btnStyle}>🔍 Load Timeline</button>
        </div>
      </div>

      {/* SEARCH & EXPORT */}
      {timelineData.length > 0 && (
          <div style={{marginTop:"20px", display:"flex", gap:"10px", justifyContent:"space-between", flexWrap:"wrap"}}>
              <input 
                type="text" 
                placeholder="🔎 Filter (e.g. Visit, Money, Hospital)..." 
                value={searchTerm} 
                onChange={(e)=>setSearchTerm(e.target.value)} 
                style={{...inputStyle, maxWidth:"300px", marginTop:0}}
              />
              <button onClick={downloadExcel} style={{...btnStyle, backgroundColor:"#27ae60", height:"auto"}}>📥 Download Report</button>
          </div>
      )}

      {/* TIMELINE DISPLAY */}
      {loading ? <Loader /> : (
          <div style={{marginTop:"30px", maxWidth:"800px"}}>
            {filteredData.length === 0 ? (
                <p style={{color:"gray", textAlign:"center", marginTop:"20px"}}>
                    {selectedEmp ? "No activities found for this date." : "Select an employee and date to view timeline."}
                </p>
            ) : (
                filteredData.map((item, index) => (
                    <div key={index} style={{display:"flex", marginBottom:"0px"}}>
                        {/* Time Column */}
                        <div style={{width:"80px", textAlign:"right", paddingRight:"20px", borderRight:"2px solid #e0e0e0", position:"relative", paddingBottom:"30px"}}>
                            <span style={{fontSize:"12px", fontWeight:"bold", color:"#555", display:"block", marginTop:"15px"}}>{item.time}</span>
                            <div style={{position:"absolute", right:"-6px", top:"20px", width:"10px", height:"10px", borderRadius:"50%", backgroundColor: item.color, border:"2px solid white", boxShadow:"0 0 3px rgba(0,0,0,0.3)"}}></div>
                        </div>
                        
                        {/* Content Column */}
                        <div style={{paddingLeft:"20px", flex:1, paddingBottom:"20px"}}>
                            <div style={{backgroundColor:"white", padding:"12px 15px", borderRadius:"8px", boxShadow:"0 2px 5px rgba(0,0,0,0.05)", borderLeft:`4px solid ${item.color}`, border:"1px solid #eee"}}>
                                <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"5px"}}>
                                    <h4 style={{margin:0, color: item.color, fontSize:"14px"}}>{item.icon} {item.type}</h4>
                                </div>
                                <p style={{margin:0, fontSize:"13px", color:"#444", lineHeight:"1.5"}}>{item.details}</p>
                            </div>
                        </div>
                    </div>
                ))
            )}
          </div>
      )}
    </div>
  );
}

// STYLES
const controlBox = {
    backgroundColor: "white", padding: "20px", borderRadius: "10px", 
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)", display: "flex", gap: "15px", flexWrap:"wrap", alignItems:"center"
};
const inputStyle = { width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc", fontSize:"14px", marginTop:"5px" };
const labelStyle = {fontSize:"12px", fontWeight:"bold", color:"#555"};
const btnStyle = { padding: "10px 20px", backgroundColor: "#2c3e50", color: "white", border: "none", borderRadius: "5px", cursor: "pointer", fontWeight:"bold", fontSize:"14px", height:"40px" };

export default Timeline;